import { useState, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import { EntradaCompleta, TipoMovimento, DocumentoTipo, ItemEntrada } from "@/types/entrada";
import { entradaAdapter, productsAdapter, unitsAdapter } from "@/data";
import { toast } from "@/hooks/use-toast";
import { formatCurrency } from "@/lib/utils";
import { v4 as uuid } from "uuid";
import { MarcaCombobox } from "./MarcaCombobox";
import { FornecedorCombobox } from "./FornecedorCombobox";

interface EntradaSimplificadaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  entrada: EntradaCompleta | null;
  onSuccess: () => void;
}

// Schema do CABEÇALHO (Nota Fiscal)
const cabecalhoSchema = z.object({
  tipo_movimento: z.string().min(1, "Tipo de movimento é obrigatório"),
  data_entrada: z.string().min(1, "Data de entrada é obrigatória"),
  fornecedor_id: z.string().uuid().optional().nullable(),
  documento_tipo: z.string().optional(),
  documento_numero: z.string().optional(),
  documento_serie: z.string().optional(),
  chave_nfe: z.string().optional(),
  observacao: z.string().optional(),
});

type CabecalhoFormValues = z.infer<typeof cabecalhoSchema>;

// Schema do ITEM (Produto)
const itemSchema = z.object({
  produto_id: z.string().min(1, "Produto é obrigatório"),
  produto_nome: z.string().min(1, "Nome do produto é obrigatório"),
  quantidade_compra: z.number().min(0.001, "Quantidade deve ser maior que 0"),
  custo_unitario_compra: z.number().min(0, "Custo unitário deve ser maior ou igual a 0"),
  outras_despesas_item: z.preprocess(
    (val) => {
      if (val === "" || val === null || val === undefined) return undefined;
      if (typeof val === "number" && isNaN(val)) return undefined;
      const num = Number(val);
      return isNaN(num) ? undefined : num;
    },
    z.number().nonnegative("Despesas não podem ser negativas").optional()
  ),
  tipo_embalagem: z.enum(['Unidade', 'Pacote', 'Caixa', 'Lata', 'Outro'], {
    required_error: "Tipo de embalagem é obrigatório"
  }),
  peso_individual_g: z.preprocess(
    (val) => {
      if (val === "" || val === null || val === undefined) return undefined;
      if (typeof val === "number" && isNaN(val)) return undefined;
      const num = Number(val);
      return isNaN(num) ? undefined : num;
    },
    z.number().positive("Peso deve ser maior que zero").optional()
  ),
  volume_individual_ml: z.preprocess(
    (val) => {
      if (val === "" || val === null || val === undefined) return undefined;
      if (typeof val === "number" && isNaN(val)) return undefined;
      const num = Number(val);
      return isNaN(num) ? undefined : num;
    },
    z.number().positive("Volume deve ser maior que zero").optional()
  ),
  lote: z.string().optional(),
  validade: z.string().optional(),
  localizacao: z.string().optional(),
  observacao_item: z.string().optional(),
  marca_id: z.string().uuid().optional(),
  forma_pagamento: z.enum([
    'credito_vista',
    'credito_parcelado',
    'dinheiro',
    'debito',
    'pix',
    'boleto',
    'cheque'
  ]).optional().nullable(),
});

type ItemFormValues = z.infer<typeof itemSchema>;

export function EntradaSimplificadaDialog({
  open,
  onOpenChange,
  mode,
  entrada,
  onSuccess,
}: EntradaSimplificadaDialogProps) {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [products, setProducts] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Form do CABEÇALHO (Nota Fiscal)
  const cabecalhoForm = useForm<CabecalhoFormValues>({
    resolver: zodResolver(cabecalhoSchema),
    defaultValues: {
      tipo_movimento: "compra",
      data_entrada: new Date().toISOString().split("T")[0],
      fornecedor_id: null,
      documento_tipo: "",
      documento_numero: "",
      documento_serie: "",
      chave_nfe: "",
      observacao: "",
    },
  });

  // Form do ITEM (Produto)
  const itemForm = useForm<ItemFormValues>({
    resolver: zodResolver(itemSchema),
    defaultValues: {
      produto_id: "",
      produto_nome: "",
      quantidade_compra: 1,
      custo_unitario_compra: 0,
      outras_despesas_item: undefined,
      tipo_embalagem: "Unidade",
      peso_individual_g: undefined,
      volume_individual_ml: undefined,
      lote: "",
      validade: "",
      localizacao: "",
      observacao_item: "",
      marca_id: "",
      forma_pagamento: null,
    },
  });

  // Carregar produtos e unidades
  useEffect(() => {
    if (open) {
      loadProducts();
      loadUnits();
    }
  }, [open]);

  // Resetar formulários ao abrir/fechar
  useEffect(() => {
    if (!open) {
      cabecalhoForm.reset({
        tipo_movimento: "compra",
        data_entrada: new Date().toISOString().split("T")[0],
        fornecedor_id: null,
        documento_tipo: "",
        documento_numero: "",
        documento_serie: "",
        chave_nfe: "",
        observacao: "",
      });
      itemForm.reset({
        produto_id: "",
        produto_nome: "",
        quantidade_compra: 1,
        custo_unitario_compra: 0,
        outras_despesas_item: undefined,
        tipo_embalagem: "Unidade",
        peso_individual_g: undefined,
        volume_individual_ml: undefined,
        lote: "",
        validade: "",
        localizacao: "",
        observacao_item: "",
        marca_id: "",
        forma_pagamento: null,
      });
      setSelectedProduct(null);
    }
  }, [open]);

  // Carregar dados da entrada no modo edit
  useEffect(() => {
    if (open && mode === "edit" && entrada) {
      // Carregar dados do cabeçalho
      cabecalhoForm.reset({
        tipo_movimento: entrada.cabecalho.tipo_movimento,
        data_entrada: entrada.cabecalho.data_entrada.split('T')[0],
        fornecedor_id: entrada.cabecalho.fornecedor_id || null,
        documento_tipo: entrada.cabecalho.documento_tipo || "",
        documento_numero: entrada.cabecalho.documento_numero || "",
        documento_serie: entrada.cabecalho.documento_serie || "",
        chave_nfe: entrada.cabecalho.chave_nfe || "",
        observacao: entrada.cabecalho.observacao || "",
      });

      // Carregar dados do primeiro item (dialog suporta apenas 1 item)
      const item = entrada.itens[0];
      if (item) {
        itemForm.reset({
          produto_id: item.produto_id,
          produto_nome: item.produto_nome,
          quantidade_compra: item.quantidade_compra,
          custo_unitario_compra: item.custo_unitario_compra,
          outras_despesas_item: item.outras_despesas_item || undefined,
          tipo_embalagem: item.tipo_embalagem,
          peso_individual_g: item.peso_individual_g || undefined,
          volume_individual_ml: item.volume_individual_ml || undefined,
          lote: item.lote || "",
          validade: item.validade ? item.validade.split('T')[0] : "",
          localizacao: item.localizacao || "",
          observacao_item: item.observacao_item || "",
          marca_id: item.marca_id || "",
          forma_pagamento: item.forma_pagamento || null,
        });
        
        // Carregar produto selecionado
        loadProductById(item.produto_id);
      }
    }
  }, [open, mode, entrada]);

  const loadProducts = async () => {
    try {
      setLoadingProducts(true);
      const result = await productsAdapter.list();
      setProducts(result.items);
    } catch (error) {
      console.error("Erro ao carregar produtos:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os produtos",
        variant: "destructive",
      });
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadUnits = async () => {
    try {
      const result = await unitsAdapter.list({});
      setUnits(result.items);
    } catch (error) {
      console.error("Erro ao carregar unidades:", error);
    }
  };

  const loadProductById = async (productId: string) => {
    try {
      const product = await productsAdapter.get(productId);
      setSelectedProduct(product);
    } catch (error) {
      console.error("Erro ao carregar produto:", error);
    }
  };

  const handleProductChange = async (productId: string) => {
    itemForm.setValue("produto_id", productId);
    const product = await productsAdapter.get(productId);
    setSelectedProduct(product);
    
    // 🔍 Log para verificar se o produto foi carregado
    console.log("🔍 Produto selecionado:", {
      id: product?.id_produto,
      nome: product?.nome,
      unidade_padrao: product?.unidade_padrao
    });
    
    itemForm.setValue("produto_nome", product?.nome || "");
    
    // 🔍 Verificar se o campo foi realmente preenchido
    console.log("🔍 Valor de produto_nome após setValue:", itemForm.getValues("produto_nome"));
  };

  const watchQuantidade = itemForm.watch("quantidade_compra");
  const watchCusto = itemForm.watch("custo_unitario_compra");
  const watchOutrasDespesas = itemForm.watch("outras_despesas_item");

  const outrasDespesas = watchOutrasDespesas || 0;
  const subtotal = (watchQuantidade * watchCusto) + outrasDespesas;

  const handleSalvar = async () => {
    // Prevenir múltiplos cliques
    if (isSubmitting) {
      console.log("⚠️ Salvamento já em andamento, ignorando clique duplicado");
      return;
    }

    // Validar cabeçalho
    const isHeaderValid = await cabecalhoForm.trigger();
    if (!isHeaderValid) {
      toast({
        title: "Dados incompletos",
        description: "Preencha todos os campos obrigatórios da nota fiscal.",
        variant: "destructive",
      });
      return;
    }

    // Validar item
    const isItemValid = await itemForm.trigger();
    if (!isItemValid) {
      // 🔍 Log detalhado dos erros
      const errors = itemForm.formState.errors;
      console.error("❌ Erros de validação do item:", errors);
      
      // 🔍 Identificar quais campos estão com erro
      const camposComErro = Object.keys(errors).map((campo) => {
        return `${campo}: ${errors[campo as keyof typeof errors]?.message}`;
      }).join(", ");
      
      console.error("❌ Campos com erro:", camposComErro);
      
      toast({
        title: "Dados incompletos",
        description: `Preencha todos os campos obrigatórios do produto. Campos com erro: ${camposComErro}`,
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      console.log("🔄 Iniciando salvamento da entrada...");

      const cabecalhoData = cabecalhoForm.getValues();
      const itemData = itemForm.getValues();
      
      const subtotalItem = (itemData.quantidade_compra * itemData.custo_unitario_compra) + (itemData.outras_despesas_item || 0);

      if (mode === "edit" && entrada) {
        // MODO EDIÇÃO: Atualizar entrada existente
        const updatePayload = {
          cabecalho: {
            tipo_movimento: cabecalhoData.tipo_movimento as TipoMovimento,
            data_entrada: cabecalhoData.data_entrada,
            fornecedor_id: cabecalhoData.fornecedor_id || null,
            documento_tipo: (cabecalhoData.documento_tipo || null) as DocumentoTipo | null,
            documento_numero: cabecalhoData.documento_numero || null,
            documento_serie: cabecalhoData.documento_serie || null,
            chave_nfe: cabecalhoData.chave_nfe || null,
            observacao: cabecalhoData.observacao || null,
          },
          itens: [{
            id: uuid(), // ✅ SEMPRE gera novo UUID na edição (DELETE + INSERT)
            produto_id: itemData.produto_id,
            produto_nome: itemData.produto_nome,
            // unidade_padrao removido - não existe na tabela (obtido via JOIN na leitura)
            quantidade_compra: itemData.quantidade_compra,
            custo_unitario_compra: itemData.custo_unitario_compra,
            outras_despesas_item: itemData.outras_despesas_item || null,
            tipo_embalagem: itemData.tipo_embalagem,
            peso_individual_g: itemData.peso_individual_g || null,
            volume_individual_ml: itemData.volume_individual_ml || null,
            lote: itemData.lote || null,
            validade: itemData.validade || null,
            localizacao: itemData.localizacao || null,
            observacao_item: itemData.observacao_item || null,
            marca_id: itemData.marca_id || null,
            forma_pagamento: itemData.forma_pagamento || null,
          }],
          totais: {
            subtotal_compra: subtotalItem,
            outras_despesas_total: itemData.outras_despesas_item || null,
            total_geral_documento: subtotalItem,
          },
        };

        await entradaAdapter.update(entrada.id, updatePayload);
        
        console.log("✅ Entrada atualizada com sucesso:", entrada.id);
        
        toast({
          title: "Sucesso",
          description: "Entrada atualizada com sucesso",
        });

        // Invalidar cache do React Query para forçar atualização do estoque
        queryClient.invalidateQueries({ queryKey: ['estoque'] });
        console.log('✅ Cache de estoque invalidado');

        // Resetar formulários e fechar dialog
        cabecalhoForm.reset();
        itemForm.reset();
        setSelectedProduct(null);
        onSuccess();
        onOpenChange(false);
      } else {
        // MODO CRIAÇÃO: Criar nova entrada
        const entradaData: EntradaCompleta = {
          id: uuid(),
          cabecalho: {
            tipo_movimento: cabecalhoData.tipo_movimento as TipoMovimento,
            data_entrada: cabecalhoData.data_entrada,
            fornecedor_id: cabecalhoData.fornecedor_id || null,
            documento_tipo: (cabecalhoData.documento_tipo || null) as DocumentoTipo | null,
            documento_numero: cabecalhoData.documento_numero || null,
            documento_serie: cabecalhoData.documento_serie || null,
            chave_nfe: cabecalhoData.chave_nfe || null,
            observacao: cabecalhoData.observacao || null,
          },
          itens: [{
            id: uuid(),
            produto_id: itemData.produto_id,
            produto_nome: itemData.produto_nome,
            // unidade_padrao removido - não existe na tabela (obtido via JOIN na leitura)
            quantidade_compra: itemData.quantidade_compra,
            custo_unitario_compra: itemData.custo_unitario_compra,
            outras_despesas_item: itemData.outras_despesas_item || null,
            tipo_embalagem: itemData.tipo_embalagem,
            peso_individual_g: itemData.peso_individual_g || null,
            volume_individual_ml: itemData.volume_individual_ml || null,
            lote: itemData.lote || null,
            validade: itemData.validade || null,
            localizacao: itemData.localizacao || null,
            observacao_item: itemData.observacao_item || null,
            marca_id: itemData.marca_id || null,
            forma_pagamento: itemData.forma_pagamento || null,
          }],
          totais: {
            subtotal_compra: subtotalItem,
            desconto_total_valor: null,
            desconto_total_pct: null,
            frete_total: null,
            outras_despesas_total: itemData.outras_despesas_item || null,
            impostos_nao_recuperaveis_total: null,
            total_geral_documento: subtotalItem,
            estrategia_rateio: "sem_rateio",
          },
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        console.log("📦 Dados preparados:", {
          entrada_id: entradaData.id,
          produto: selectedProduct?.nome,
          quantidade: itemData.quantidade_compra,
        });

        const resultado = await entradaAdapter.create(entradaData);
        
        if (resultado?.id) {
          console.log("✅ Entrada criada com sucesso:", resultado.id);
          
          toast({
            title: "Sucesso",
            description: "Produto adicionado ao estoque",
          });

          // Invalidar cache do React Query para forçar atualização do estoque
          queryClient.invalidateQueries({ queryKey: ['estoque'] });
          console.log('✅ Cache de estoque invalidado');

          // Resetar formulários
          cabecalhoForm.reset();
          itemForm.reset();
          setSelectedProduct(null);
          
          // Callbacks de sucesso
          onSuccess();
          onOpenChange(false);
        } else {
          console.error("⚠️ Resposta inesperada do servidor:", resultado);
          throw new Error("Resposta inesperada do servidor");
        }
      }
      
    } catch (error: any) {
      // ✅ Log detalhado do erro
      console.error("❌ Erro ao salvar entrada:", {
        message: error?.message,
        code: error?.code,
        details: error?.details,
        hint: error?.hint,
      });
      
      // ✅ Mensagens de erro específicas por tipo
      let errorTitle = "Erro ao salvar";
      let errorMessage = "Não foi possível salvar a entrada";
      
      // Erro de permissão/RLS
      if (error?.code === '42501' || error?.message?.includes('policy')) {
        errorTitle = "Sem permissão";
        errorMessage = "Você não tem permissão para adicionar produtos ao estoque";
      }
      // Erro de chave estrangeira (produto não existe)
      else if (error?.code === '23503') {
        errorTitle = "Produto inválido";
        errorMessage = "O produto selecionado não existe ou foi removido";
      }
      // Erro de constraint única
      else if (error?.code === '23505') {
        errorTitle = "Duplicado";
        errorMessage = "Já existe uma entrada com estes dados";
      }
      // Outros erros com mensagem
      else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: errorTitle,
        description: errorMessage,
        variant: "destructive",
      });
      
      // ✅ Dialog permanece aberto para o usuário tentar novamente
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0 pb-4">
          <DialogTitle>
            {mode === "edit" ? "Editar Entrada no Estoque" : "Adicionar Produto ao Estoque"}
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 -mx-6 px-6">
        <div className="space-y-6">
          {/* SEÇÃO 1: DADOS DA NOTA FISCAL (FIXO) */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Dados da Nota Fiscal</CardTitle>
              <CardDescription>Informações da compra (preenchidas uma vez)</CardDescription>
            </CardHeader>
            <CardContent>
              <FormProvider {...cabecalhoForm}>
                <form className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="tipo_movimento">Tipo de Movimento *</Label>
                    <Select
                      value={cabecalhoForm.watch("tipo_movimento")}
                      onValueChange={(value) => cabecalhoForm.setValue("tipo_movimento", value)}
                    >
                      <SelectTrigger className={cabecalhoForm.formState.errors.tipo_movimento ? "border-destructive" : ""}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="compra">Compra</SelectItem>
                        <SelectItem value="bonificacao">Bonificação</SelectItem>
                        <SelectItem value="devolucao_cliente">Devolução Cliente</SelectItem>
                        <SelectItem value="ajuste_positivo">Ajuste Positivo</SelectItem>
                      </SelectContent>
                    </Select>
                    {cabecalhoForm.formState.errors.tipo_movimento && (
                      <p className="text-sm text-destructive">{cabecalhoForm.formState.errors.tipo_movimento.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="data_entrada">Data da Entrada *</Label>
                    <Input
                      id="data_entrada"
                      type="date"
                      {...cabecalhoForm.register("data_entrada")}
                      className={cabecalhoForm.formState.errors.data_entrada ? "border-destructive" : ""}
                    />
                    {cabecalhoForm.formState.errors.data_entrada && (
                      <p className="text-sm text-destructive">{cabecalhoForm.formState.errors.data_entrada.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="fornecedor_id">Fornecedor</Label>
                    <FornecedorCombobox
                      value={cabecalhoForm.watch("fornecedor_id") || ""}
                      onValueChange={(value) => cabecalhoForm.setValue("fornecedor_id", value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="documento_tipo">Tipo de Documento</Label>
                    <Select
                      value={cabecalhoForm.watch("documento_tipo") || ""}
                      onValueChange={(value) => cabecalhoForm.setValue("documento_tipo", value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NFe">NFe</SelectItem>
                        <SelectItem value="Cupom">Cupom</SelectItem>
                        <SelectItem value="Pedido">Pedido</SelectItem>
                        <SelectItem value="Outro">Outro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="documento_numero">Número</Label>
                    <Input id="documento_numero" {...cabecalhoForm.register("documento_numero")} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="documento_serie">Série</Label>
                    <Input id="documento_serie" {...cabecalhoForm.register("documento_serie")} />
                  </div>

                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="chave_nfe">Chave NFe</Label>
                    <Input
                      id="chave_nfe"
                      {...cabecalhoForm.register("chave_nfe")}
                      placeholder="44 dígitos"
                      maxLength={44}
                    />
                  </div>

                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="observacao">Observações</Label>
                    <Textarea id="observacao" {...cabecalhoForm.register("observacao")} rows={2} />
                  </div>
                </form>
              </FormProvider>
            </CardContent>
          </Card>

          {/* SEÇÃO 2: FORMULÁRIO DO PRODUTO */}
          <FormProvider {...itemForm}>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Adicionar Produto</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="produto_id">Produto *</Label>
                  <Select
                    value={itemForm.watch("produto_id") || ""}
                    onValueChange={handleProductChange}
                    disabled={loadingProducts}
                  >
                    <SelectTrigger className={itemForm.formState.errors.produto_id ? "border-destructive" : ""}>
                      <SelectValue placeholder={loadingProducts ? "Carregando..." : "Selecione um produto..."} />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((product) => (
                        <SelectItem key={product.id_produto} value={product.id_produto}>
                          {product.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {itemForm.formState.errors.produto_id && (
                    <p className="text-sm text-destructive">{itemForm.formState.errors.produto_id.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="quantidade_compra">Quantidade *</Label>
                  <Input
                    id="quantidade_compra"
                    type="number"
                    step="0.001"
                    {...itemForm.register("quantidade_compra", { valueAsNumber: true })}
                    className={itemForm.formState.errors.quantidade_compra ? "border-destructive" : ""}
                  />
                  {itemForm.formState.errors.quantidade_compra && (
                    <p className="text-sm text-destructive">{itemForm.formState.errors.quantidade_compra.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="custo_unitario_compra">Custo Unitário *</Label>
                  <Input
                    id="custo_unitario_compra"
                    type="number"
                    step="0.01"
                    {...itemForm.register("custo_unitario_compra", { valueAsNumber: true })}
                    className={itemForm.formState.errors.custo_unitario_compra ? "border-destructive" : ""}
                  />
                  {itemForm.formState.errors.custo_unitario_compra && (
                    <p className="text-sm text-destructive">{itemForm.formState.errors.custo_unitario_compra.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="outras_despesas_item">Outras Despesas (R$)</Label>
                  <Input
                    id="outras_despesas_item"
                    type="text"
                    inputMode="decimal"
                    step="0.01"
                    placeholder="0.00"
                    className={itemForm.formState.errors.outras_despesas_item ? "border-destructive" : ""}
                    {...itemForm.register("outras_despesas_item", { 
                      setValueAs: (v) => {
                        if (v === "" || v === null || v === undefined) return undefined;
                        const num = parseFloat(v);
                        return isNaN(num) ? undefined : num;
                      }
                    })}
                  />
                  {itemForm.formState.errors.outras_despesas_item && (
                    <p className="text-sm text-destructive">
                      {itemForm.formState.errors.outras_despesas_item.message}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Frete, seguro e outras despesas deste item
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tipo_embalagem">Tipo de Embalagem *</Label>
                  <Select 
                    value={itemForm.watch("tipo_embalagem")} 
                    onValueChange={(value) => itemForm.setValue("tipo_embalagem", value as any)}
                  >
                    <SelectTrigger className={itemForm.formState.errors.tipo_embalagem ? "border-destructive" : ""}>
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Unidade">Unidade</SelectItem>
                      <SelectItem value="Pacote">Pacote</SelectItem>
                      <SelectItem value="Caixa">Caixa</SelectItem>
                      <SelectItem value="Lata">Lata</SelectItem>
                      <SelectItem value="Outro">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                  {itemForm.formState.errors.tipo_embalagem && (
                    <p className="text-sm text-destructive">
                      {itemForm.formState.errors.tipo_embalagem.message}
                    </p>
                  )}
                  {itemForm.formState.errors.tipo_embalagem && (
                    <p className="text-sm text-destructive">
                      {itemForm.formState.errors.tipo_embalagem.message}
                    </p>
                  )}
                  {itemForm.formState.errors.tipo_embalagem && (
                    <p className="text-sm text-destructive">{itemForm.formState.errors.tipo_embalagem.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="peso_individual_g">
                    Peso Individual (g) <span className="text-muted-foreground text-xs">(opcional)</span>
                  </Label>
                  <Input 
                    id="peso_individual_g" 
                    type="text"
                    inputMode="decimal"
                    placeholder="Ex: 500" 
                    {...itemForm.register("peso_individual_g", { 
                      setValueAs: (v) => {
                        if (v === "" || v === null || v === undefined) return undefined;
                        const num = parseFloat(v);
                        return isNaN(num) ? undefined : num;
                      }
                    })} 
                  />
                  {itemForm.formState.errors.peso_individual_g && (
                    <p className="text-sm text-destructive">{itemForm.formState.errors.peso_individual_g.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="volume_individual_ml">
                    Volume Individual (ml) <span className="text-muted-foreground text-xs">(opcional)</span>
                  </Label>
                  <Input 
                    id="volume_individual_ml" 
                    type="text"
                    inputMode="decimal"
                    placeholder="Ex: 1000" 
                    {...itemForm.register("volume_individual_ml", { 
                      setValueAs: (v) => {
                        if (v === "" || v === null || v === undefined) return undefined;
                        const num = parseFloat(v);
                        return isNaN(num) ? undefined : num;
                      }
                    })} 
                  />
                  {itemForm.formState.errors.volume_individual_ml && (
                    <p className="text-sm text-destructive">{itemForm.formState.errors.volume_individual_ml.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lote">Lote</Label>
                  <Input id="lote" {...itemForm.register("lote")} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="validade">Validade</Label>
                  <Input id="validade" type="date" {...itemForm.register("validade")} />
                </div>

                <div className="col-span-2 space-y-2">
                  <Label htmlFor="localizacao">Localização</Label>
                  <Input id="localizacao" {...itemForm.register("localizacao")} placeholder="Ex: Depósito A, Prateleira 3" />
                </div>

                <div className="col-span-2 space-y-2">
                  <Label htmlFor="observacao_item">Observações do Item</Label>
                  <Textarea id="observacao_item" {...itemForm.register("observacao_item")} rows={2} />
                </div>
              </CardContent>
            </Card>

            {/* INFORMAÇÕES COMERCIAIS */}
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-lg">Informações Comerciais</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="marca_id">Marca</Label>
                  <MarcaCombobox
                    value={itemForm.watch("marca_id") || ""}
                    onValueChange={(value) => itemForm.setValue("marca_id", value)}
                  />
                  {itemForm.formState.errors.marca_id && (
                    <p className="text-sm text-destructive">{itemForm.formState.errors.marca_id.message}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Marca comercial do produto
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="forma_pagamento">Forma de Pagamento</Label>
                  <Select
                    value={itemForm.watch("forma_pagamento") || ""}
                    onValueChange={(value) => {
                      if (!value) {
                        itemForm.setValue("forma_pagamento", null);
                      } else {
                        itemForm.setValue("forma_pagamento", value as any);
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="credito_vista">Crédito à Vista</SelectItem>
                      <SelectItem value="credito_parcelado">Crédito Parcelado</SelectItem>
                      <SelectItem value="dinheiro">Dinheiro</SelectItem>
                      <SelectItem value="debito">Débito</SelectItem>
                      <SelectItem value="pix">PIX</SelectItem>
                      <SelectItem value="boleto">Boleto</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Forma de pagamento específica para este item
                  </p>
                </div>

                <div className="col-span-2 border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Subtotal do Item:</span>
                    <span className="text-lg font-bold">{formatCurrency(subtotal)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </FormProvider>

        </div>
        </div>

        <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSalvar}
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isSubmitting ? "Salvando..." : (mode === "edit" ? "Atualizar Entrada" : "Salvar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
